// v3.17.0 — Panier Famille : page publique où le proche (diaspora) voit le
// panier préparé à Abidjan et le paie en 1 clic avec son wallet AYA.

import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import AuthModal from "@/components/AuthModal";
import { useAuth } from "@/hooks/useAuth";

const API = process.env.REACT_APP_BACKEND_URL || "";
const fmt = (n) => `${Number(n || 0).toLocaleString("fr-FR")} FCFA`;

export default function FamilyBasketPage() {
  const { basketId } = useParams();
  const { user, token } = useAuth();
  const [basket, setBasket] = useState(null);
  const [err, setErr] = useState(null);
  const [payState, setPayState] = useState("idle"); // idle|loading|done|error
  const [payMethod, setPayMethod] = useState(null); // wallet | card
  const [payErr, setPayErr] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/v3/family-basket/${basketId}`);
      const d = await r.json();
      if (!r.ok || !d.ok) { setErr(d.error || "introuvable"); return; }
      setBasket(d.basket);
    } catch (_) { setErr("réseau"); }
  }, [basketId]);

  useEffect(() => { load(); }, [load]);

  const pay = async (method = "wallet") => {
    setPayState("loading"); setPayMethod(method); setPayErr(null);
    try {
      const r = await fetch(`${API}/api/v3/family-basket/${basketId}/pay`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("civ_user_token")}`,
        },
        body: JSON.stringify({ method }),
      });
      const d = await r.json();
      if (!r.ok || !d.ok) {
        setPayState("error");
        setPayErr(d.error === "insufficient_funds"
          ? `Solde wallet insuffisant (${fmt(d.balance_fcfa)} disponible, ${fmt(d.needed_fcfa)} requis). Recharge ton wallet dans Mon Compte.`
          : d.error === "already_paid" ? `Ce panier a déjà été payé${d.payer_name ? ` par ${d.payer_name}` : ""} 💛` : "Paiement impossible, réessaie.");
        return;
      }
      setPayState("done");
      load();
    } catch (_) { setPayState("error"); setPayErr("Erreur réseau, réessaie."); }
  };

  const S = {
    page: { minHeight: "100vh", background: "linear-gradient(160deg,#06231A,#0B4531)", color: "#F4F1E8", padding: "24px 16px", fontFamily: "inherit" },
    card: { maxWidth: 480, margin: "0 auto", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(16,185,129,0.3)", borderRadius: 18, padding: 20 },
  };

  if (err) return <div style={S.page}><div style={S.card} data-testid="family-basket-error">😕 Panier introuvable ou lien expiré.</div></div>;
  if (!basket) return <div style={S.page}><div style={S.card}>⏳ Chargement du panier…</div></div>;

  const paid = basket.status === "paid" || payState === "done";

  return (
    <div style={S.page} data-testid="family-basket-page">
      <div style={S.card}>
        <div style={{ fontSize: 12, letterSpacing: 2, color: "#34D399", fontWeight: 800, marginBottom: 6 }}>💛 AYA · PANIER FAMILLE</div>
        <h1 style={{ fontSize: 20, fontWeight: 800, margin: "0 0 4px" }}>
          {basket.creator_name} compte sur toi 🇨🇮
        </h1>
        <div style={{ fontSize: 12.5, opacity: 0.7, marginBottom: 14 }}>
          Panier préparé à Abidjan par {basket.creator_name} ({basket.creator_phone_masked}). Tu paies, AYA livre chez lui/elle.
        </div>
        {basket.note && (
          <div style={{ fontSize: 13, fontStyle: "italic", background: "rgba(239,199,94,0.12)", border: "1px solid rgba(239,199,94,0.4)", borderRadius: 10, padding: "8px 12px", marginBottom: 14 }}>
            🗣 « {basket.note} »
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
          {basket.items.map((it, i) => (
            <div key={i} data-testid={`family-basket-item-${i}`} style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 13.5, borderBottom: "1px dashed rgba(255,255,255,0.12)", paddingBottom: 6 }}>
              <span>{it.name} <span style={{ opacity: 0.6 }}>×{it.qty}</span><br /><span style={{ fontSize: 11, opacity: 0.55 }}>🏪 {it.merchant_name}</span></span>
              <strong>{fmt(it.price_fcfa * it.qty)}</strong>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16, fontWeight: 800, marginBottom: 16 }}>
          <span>Total</span><span data-testid="family-basket-total" style={{ color: "#EFC75E" }}>{fmt(basket.total_fcfa)}</span>
        </div>

        {paid ? (
          <div data-testid="family-basket-paid" style={{ background: "rgba(52,211,153,0.15)", border: "1px solid #34D399", borderRadius: 12, padding: "14px 16px", fontSize: 14, fontWeight: 700 }}>
            ✅ Panier payé{basket.payer_name ? ` par ${basket.payer_name}` : ""} — la livraison est en route chez {basket.creator_name} 💛
          </div>
        ) : !token ? (
          <>
            <button data-testid="family-basket-login-btn" onClick={() => setAuthOpen(true)} style={{ width: "100%", borderRadius: 999, padding: "13px 0", border: "none", cursor: "pointer", background: "linear-gradient(135deg,#34D399,#10B981)", color: "#062A1F", fontWeight: 800, fontSize: 14 }}>
              🔐 Se connecter pour payer {fmt(basket.total_fcfa)}
            </button>
            <div style={{ fontSize: 11.5, opacity: 0.6, marginTop: 8, textAlign: "center" }}>Compte AYA gratuit — 30 secondes, même depuis l'étranger 🌍</div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 12, fontWeight: 700, opacity: 0.75, marginBottom: 8 }}>Choisis ton moyen de paiement :</div>
            <button data-testid="family-basket-pay-card-btn" onClick={() => pay("card")} disabled={payState === "loading"} style={{ width: "100%", borderRadius: 999, padding: "13px 0", border: "none", cursor: "pointer", background: "linear-gradient(135deg,#60A5FA,#3B82F6)", color: "#0A1A33", fontWeight: 800, fontSize: 14.5, opacity: payState === "loading" ? 0.6 : 1, marginBottom: 8 }}>
              {payState === "loading" && payMethod === "card" ? "⏳ Paiement carte en cours…" : `💳 Payer par carte bancaire · ${fmt(basket.total_fcfa)}`}
            </button>
            <button data-testid="family-basket-pay-btn" onClick={() => pay("wallet")} disabled={payState === "loading"} style={{ width: "100%", borderRadius: 999, padding: "13px 0", border: "none", cursor: "pointer", background: "linear-gradient(135deg,#EFC75E,#D9A83C)", color: "#1A1206", fontWeight: 800, fontSize: 14.5, opacity: payState === "loading" ? 0.6 : 1 }}>
              {payState === "loading" && payMethod === "wallet" ? "⏳ Paiement wallet en cours…" : `💛 Payer avec mon wallet AYA · ${fmt(basket.total_fcfa)}`}
            </button>
            <div style={{ fontSize: 11.5, opacity: 0.6, marginTop: 8, textAlign: "center" }}>Connecté : {user?.display_name || user?.phone} · carte Visa/Mastercard (démo) ou wallet AYA 🌍</div>
          </>
        )}
        {payErr && <div data-testid="family-basket-pay-error" style={{ marginTop: 10, fontSize: 12.5, color: "#FCA5A5" }}>{payErr}</div>}
      </div>
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
    </div>
  );
}
